import { useState, useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Card,
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "src/components/snackbar";
import { useLocales } from "src/locales";
import { useNavigate } from "react-router-dom";
import { PATH_KYC } from "src/routes/paths";
import Iconify from "src/components/iconify";
import { kycApi } from "src/services/kycApi";

export default function PreviewSimplified({ handleBack, onCompleteFlow, uniqueId }) {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [partyData, setPartyData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]); // To map codes to names

  // Document preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch document types and load data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch document types first (to map codes to names)
        const typesRes = await kycApi.getDocumentTypes();
        const docTypes = typesRes.data.data || [];
        setDocumentTypes(docTypes);

        // Fetch KYC data from API if uniqueId exists
        if (uniqueId) {
          try {
            console.log("Fetching KYC data for preview:", uniqueId);
            const kycDataRes = await kycApi.getKycData(uniqueId);
            const kycData = kycDataRes.data.data || kycDataRes.data;
            
            if (kycData) {
              setPartyData(kycData);
              console.log("✅ Loaded party data from API:", kycData);
            } else {
              console.log("No KYC data found in API response");
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              console.log("No KYC data found - user needs to complete Step 1");
            } else {
              console.error("Error fetching KYC data:", error);
              enqueueSnackbar("Failed to load KYC data", { variant: "error" });
            }
          }

          // Fetch documents from API
          try {
            console.log("Fetching documents for preview:", uniqueId);
            const docsRes = await kycApi.getDocuments(uniqueId);
            const documentsData = docsRes.data.data || docsRes.data || [];
            
            console.log("📄 Raw documents data from API:", documentsData);
            
            if (Array.isArray(documentsData)) {
              // Map API response to our document format (using docTypes from above)
              const mappedDocuments = documentsData.map(doc => {
                console.log("🔍 Processing document:", doc);
                console.log("🔍 Available keys:", Object.keys(doc));
                
                // Parse metadata if it's a string
                let parsedMetadata = {};
                if (doc.metadata) {
                  if (typeof doc.metadata === 'string') {
                    try {
                      // Replace single quotes with double quotes for valid JSON
                      const jsonString = doc.metadata.replace(/'/g, '"');
                      parsedMetadata = JSON.parse(jsonString);
                    } catch (e) {
                      console.warn("Failed to parse metadata string:", e);
                      parsedMetadata = {};
                    }
                  } else if (typeof doc.metadata === 'object') {
                    parsedMetadata = doc.metadata;
                  }
                }
                
                // Try multiple possible field names for identifier
                let identifier = doc.identifier || 
                               doc.document_identifier || 
                               doc.identifier_name ||
                               doc.document_name ||
                               '';
                
                // If no identifier found, generate one from document_type_code and id
                if (!identifier) {
                  const docTypeCode = doc.document_type_code || 
                                    docTypes.find(dt => dt.id === doc.document_type_id)?.buckzy_code || 
                                    'DOC';
                  const docId = doc.id || doc.document_id || '';
                  identifier = docId ? `${docTypeCode}_${docId}` : docTypeCode;
                }
                
                // Extract file_name from metadata or use fallback
                const fileName = doc.file_name || 
                               parsedMetadata.file_name || 
                               doc.name || 
                               doc.fileName || 
                               'Document';
                
                // Extract file_type from metadata
                const fileType = doc.file_type || parsedMetadata.file_type || null;
                
                console.log("🔍 Extracted identifier:", identifier);
                console.log("🔍 Extracted fileName:", fileName);
                
                return {
                  id: doc.document_id || doc.id,
                  document_id: doc.document_id || doc.id,
                  document_type_id: doc.document_type_id,
                  document_type_name: doc.document_type_name || docTypes.find(dt => dt.id === doc.document_type_id)?.name || 'Unknown',
                  document_type_code: doc.document_type_code,
                  uploaded_file_id: doc.uploaded_file_id || doc.file_id,
                  file_id: doc.uploaded_file_id || doc.file_id,
                  url: doc.url || doc.file_url,
                  fileName: fileName,
                  name: fileName,
                  identifier: identifier,
                  metadata: parsedMetadata,
                  type: doc.document_type_id,
                  fileType: fileType,
                };
              });
              
              setDocuments(mappedDocuments);
              console.log("✅ Loaded documents from API:", mappedDocuments);
            } else {
              console.log("No documents found in API response");
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              console.log("No documents found - user needs to complete Step 2");
            } else {
              console.error("Error fetching documents:", error);
              enqueueSnackbar("Failed to load documents", { variant: "error" });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar("Failed to load KYC data", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uniqueId, enqueueSnackbar]);

  // Helper function to get document type name from Buckzy code
  const getDocumentTypeName = (buckzyCode) => {
    const docType = documentTypes.find(
      (dt) => dt.buckzy_code === buckzyCode || dt.id === buckzyCode
    );
    return docType ? docType.name : buckzyCode; // Fallback to code if not found
  };

  // Helper functions to extract display values from _obj fields
  const getDisplayValue = (obj, fallback) => {
    if (obj && typeof obj === 'object' && obj.name) {
      return obj.name;
    }
    return fallback || "-";
  };

  const getOrganizationType = () => {
    return getDisplayValue(partyData?.organizationType_obj, partyData?.organizationType);
  };

  const getLineOfBusiness = () => {
    return getDisplayValue(partyData?.lineOfBusiness_obj, partyData?.lineOfBusiness);
  };

  const getCity = () => {
    return getDisplayValue(partyData?.city_obj, partyData?.city);
  };

  const getProvince = () => {
    return getDisplayValue(partyData?.province_obj, partyData?.province);
  };

  const getCountry = () => {
    return getDisplayValue(partyData?.country_obj, partyData?.country);
  };

  const getCountryOfRegistration = () => {
    return getDisplayValue(partyData?.countryOfRegistration_obj, partyData?.countryOfRegistration);
  };

  const getCountryOfOperation = () => {
    return getDisplayValue(partyData?.countryOfOperation_obj, partyData?.countryOfOperation);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Validate unique_key is present
      if (!uniqueId) {
        enqueueSnackbar("Unique key is required to submit KYC", { variant: "error" });
        setSubmitting(false);
        return;
      }

      // Build payload with only unique_key
      const payload = {
        unique_key: uniqueId
      };

      console.log("Completing KYC with payload:", payload);

      // Complete KYC
      const response = await kycApi.completeKyc(uniqueId);
      const statusCode = response?.status || response?.response?.status;

      if (statusCode === 200) {
        // Show success screen
        setIsSuccess(true);
        enqueueSnackbar("KYC submitted successfully for admin review", { variant: "success" });
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("KYC completion error:", error);
      const statusCode = error?.response?.status;
      
      if (statusCode === 400) {
        // Already submitted or validation error
        const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || "KYC has already been submitted or validation error";
        enqueueSnackbar(errorMessage, { variant: "error" });
      } else {
        enqueueSnackbar(
          error?.response?.data?.message || error?.response?.data?.detail || "Failed to submit KYC",
          { variant: "error" }
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to check if file is PDF or document file
  const isDocumentFile = (fileName, fileType) => {
    if (!fileName && !fileType) return false;
    
    const fileNameLower = (fileName || "").toLowerCase();
    const fileTypeLower = (fileType || "").toLowerCase();
    
    // Check for PDF
    if (fileNameLower.endsWith('.pdf') || fileTypeLower === 'application/pdf') {
      return true;
    }
    
    // Check for document files
    const documentExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.rtf', '.odt', '.ods'];
    const documentMimeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/rtf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet'
    ];
    
    return documentExtensions.some(ext => fileNameLower.endsWith(ext)) ||
           documentMimeTypes.some(mime => fileTypeLower.includes(mime));
  };

  // Preview document (using url from /file endpoint if available)
  const previewDocumentHandler = async (doc) => {
    try {
      console.log("Previewing document:", doc);

      // If document has url from /file endpoint, check if it's a document file
      if (doc.url) {
        const fileName = doc.fileName || doc.filename || doc.file_name || doc.name || doc.identifier || "Document";
        const fileType = doc.fileType || null;
        
        // If it's a PDF or document file, open in new tab
        if (isDocumentFile(fileName, fileType)) {
          console.log("Opening document in new tab:", doc.url);
          window.open(doc.url, '_blank');
          return;
        }
        
        // Otherwise, show in modal (for images)
        console.log("Using URL from /file endpoint in modal:", doc.url);
        setLoadingPreview(true);
        setPreviewOpen(true);
        setPreviewDocument({
          name: doc.identifier || doc.name || `Document ${doc.id}`,
          type: doc.type,
          fileName: fileName,
          url: doc.url, // Use URL from /file endpoint
          fileType: fileType
        });
        setLoadingPreview(false);
        return;
      }

      // Fallback: Fetch document content from backend API
      setLoadingPreview(true);
      setPreviewOpen(true);
      console.log("Fetching document preview from API for:", doc);

      const response = await kycApi.previewDocument(doc.id);

      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Document data:", response.data.data);

      const docData = response.data.data;

      console.log("Available keys in docData:", Object.keys(docData || {}));
      console.log("docData.document:", docData.document ? "exists" : "missing");
      console.log("docData.file:", docData.file ? "exists" : "missing");

      // Create preview data
      setPreviewDocument({
        name: doc.identifier || `Document ${doc.id}`,
        type: doc.type,
        content:
          docData.document || docData.file || docData.base64 || docData.content, // Try different possible field names
        fileName: docData.fileName || doc.identifier,
        url: null
      });
    } catch (error) {
      console.error("Error loading document preview:", error);
      enqueueSnackbar("Failed to load document preview", { variant: "error" });
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Close preview modal
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  if (loading) {
    return (
      <Card sx={{ p: 3, mt: 4 }}>
        <Typography>Loading KYC data...</Typography>
      </Card>
    );
  }

  // Show success screen after successful submission
  if (isSuccess) {
    return (
      <Card
        sx={{
          p: 6,
          mt: 4,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            bgcolor: "success.lighter",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Iconify
            icon="eva:checkmark-circle-2-fill"
            sx={{ width: 80, height: 80, color: "success.main" }}
          />
        </Box>

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          KYC Submitted Successfully!
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
          Your KYC application has been submitted successfully. Our team will review your
          information and documents. You will be notified once the verification process is
          complete.
        </Typography>
      </Card>
    );
  }

  if (!partyData) {
    return (
      <Card sx={{ p: 3, mt: 4 }}>
        <Alert severity="warning">
          No KYC data found. Please complete the business details first.
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {"Review and Submit"}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Please review all information before submitting. All data is stored
        securely in Buckzy.
      </Alert>

      {/* Removed partyId, status, and other API-only fields - only show data from steps 1 and 2 */}

      {/* Business Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Business Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Legal Name
            </Typography>
            <Typography variant="body2">
              {partyData.legalName || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Organization Type
            </Typography>
            <Typography variant="body2">
              {getOrganizationType()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Line of Business
            </Typography>
            <Typography variant="body2">
              {getLineOfBusiness()}
            </Typography>
          </Grid>
          {/* <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              First Name
            </Typography>
            <Typography variant="body2">
              {partyData.firstName || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Last Name
            </Typography>
            <Typography variant="body2">{partyData.lastName || "-"}</Typography>
          </Grid> */}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Tax ID
            </Typography>
            <Typography variant="body2">{partyData.taxId || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Registration Number
            </Typography>
            <Typography variant="body2">
              {partyData.registrationNumber || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Establishment Date
            </Typography>
            <Typography variant="body2">
              {partyData.establishmentDate || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Country of Registration
            </Typography>
            <Typography variant="body2">
              {getCountryOfRegistration()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Country of Operation
            </Typography>
            <Typography variant="body2">
              {getCountryOfOperation()}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Contact Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Contact Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Email Address
            </Typography>
            <Typography variant="body2">
              {partyData.emailAddress || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Mobile Number
            </Typography>
            <Typography variant="body2">
              {partyData.mobileCountryCode && partyData.mobileNumber
                ? `+${partyData.mobileCountryCode} ${partyData.mobileNumber}`
                : "-"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Address */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Address
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Address Line 1
            </Typography>
            <Typography variant="body2">
              {partyData.addressLine1 || "-"}
            </Typography>
          </Grid>
          {partyData.addressLine2 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Address Line 2
              </Typography>
              <Typography variant="body2">
                {partyData.addressLine2}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              City
            </Typography>
            <Typography variant="body2">
              {getCity()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              State
            </Typography>
            <Typography variant="body2">
              {getProvince()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Postal Code
            </Typography>
            <Typography variant="body2">
              {partyData.postalCode || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Country
            </Typography>
            <Typography variant="body2">
              {getCountry()}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Documents */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Uploaded Documents
        </Typography>
        {documents.length > 0 ? (
          <Stack spacing={2}>
            {documents.map((doc, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Document Type
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {doc.document_type_name || getDocumentTypeName(doc.type) || doc.documentType || `Document ${index + 1}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Identifier
                    </Typography>
                    <Typography variant="body2">
                      {doc.identifier || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      File Name
                    </Typography>
                    <Typography variant="body2">
                      {doc.fileName ||
                        doc.filename ||
                        doc.file_name ||
                        doc.name ||
                        doc.identifier ||
                        "Document file"}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={3}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {doc.status && (
                      <Chip
                        label={doc.status}
                        size="small"
                        color={
                          doc.status === "VERIFIED" ? "success" : "default"
                        }
                      />
                    )}
                    <IconButton
                      color="primary"
                      onClick={() => previewDocumentHandler(doc)}
                      title="Preview document"
                      size="small"
                    >
                      <Iconify icon="eva:eye-outline" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
        ) : (
          <Alert severity="warning">No documents uploaded yet</Alert>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button onClick={handleBack} disabled={submitting}>
          {t("back")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          onClick={handleSubmit}
        >
          Submit KYC
        </LoadingButton>
      </Stack>

      {/* Document Preview Modal */}
      <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          {loadingPreview
            ? "Loading document..."
            : previewDocument?.fileName || "Document Preview"}
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <Typography>Loading document preview...</Typography>
            </Box>
          ) : previewDocument ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
                bgcolor: "grey.100",
                borderRadius: 1,
                p: 2,
              }}
            >
              {previewDocument.url ? (
                // Use URL from /file endpoint
                <>
                  {previewDocument.fileType === "application/pdf" || previewDocument.fileName?.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={previewDocument.url}
                      style={{
                        width: "100%",
                        height: "600px",
                        border: "none"
                      }}
                      title={previewDocument.fileName}
                    />
                  ) : (
                    <img
                      src={previewDocument.url}
                      alt={previewDocument.fileName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "600px",
                        objectFit: "contain"
                      }}
                      onError={(e) => {
                        // If image fails, try as iframe (might be PDF)
                        e.target.style.display = "none";
                        const iframe = document.createElement("iframe");
                        iframe.src = previewDocument.url;
                        iframe.style.width = "100%";
                        iframe.style.height = "600px";
                        iframe.style.border = "none";
                        e.target.parentElement.appendChild(iframe);
                      }}
                    />
                  )}
                </>
              ) : previewDocument.content ? (
                // Fallback to base64 content
                <>
                  {previewDocument.fileType === "application/pdf" || previewDocument.fileName?.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={`data:application/pdf;base64,${previewDocument.content}`}
                      style={{
                        width: "100%",
                        height: "600px",
                        border: "none"
                      }}
                      title={previewDocument.fileName}
                    />
                  ) : (
                    <img
                      src={`data:image/jpeg;base64,${previewDocument.content}`}
                      alt={previewDocument.fileName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "600px",
                        objectFit: "contain"
                      }}
                      onError={(e) => {
                        // If image fails, try PDF
                        e.target.style.display = "none";
                        const iframe = document.createElement("iframe");
                        iframe.src = `data:application/pdf;base64,${previewDocument.content}`;
                        iframe.style.width = "100%";
                        iframe.style.height = "600px";
                        iframe.style.border = "none";
                        e.target.parentElement.appendChild(iframe);
                      }}
                    />
                  )}
                </>
              ) : (
                <Typography color="text.secondary">
                  No preview available
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
